import React, {
  useContext,
  createContext,
  ReactNode,
  useState,
  memo,
} from 'react';
import {
  MatchAggregated,
  Round,
} from 'functions/src/types/firestore/Game/Tournament/Bracket';
import { MatchDetails, MatchDetailsProps } from './MatchDetails';
import { MatchProvider } from '../MatchProvider';

export type MatchDetailsContextType = {
  MatchDetails?: ReactNode;
  populate: (match: MatchAggregated<Date>) => void;
  reset: () => void;
};

export const MatchDetailsContext =
  createContext<MatchDetailsContextType | null>(null);

export const useMatchDetails = (): MatchDetailsContextType => {
  const context = useContext(MatchDetailsContext);
  if (!context) {
    throw new Error(
      'useMatchDetails must be used within a MatchDetailsProvider',
    );
  }
  return context;
};

export const MatchDetailsProvider = memo(function MatchDetailsProvider({
  children,
  rounds,
}: {
  rounds: Round<Date>[];
  children: ReactNode;
}) {
  const [matchDetails, setMatchDetails] = useState<MatchDetailsProps | null>(
    null,
  );
  const [match, setMatch] = useState<MatchAggregated<Date> | null>(null);

  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const populate = (match: MatchAggregated<Date>) => {
    const { id, startTime } = match;
    const round = rounds.find((round) => {
      return round.matches?.find((match) => {
        return match.id === id;
      });
    });
    const reward = !!round?.payout
      ? { currency: 'USD', amount: round?.payout }
      : undefined;

    const roundPreviousId = round?.previous;
    const roundPrevious = rounds.find((round) => {
      return round.id === roundPreviousId;
    });
    const rewardSecured = !!roundPrevious?.payout
      ? {
          currency: 'USD',
          amount: roundPrevious?.payout,
        }
      : undefined;
    const matchDetailsProps: MatchDetailsProps = {
      id,
      reward,
      // status,
      rewardSecured,
      startTime,
      sx: { minWidth: '450px' },
    };
    setMatchDetails(matchDetailsProps);
    setMatch(match);
  };

  const reset = () => {
    setMatchDetails(null);
  };

  const MatchDetailsRendered = !!matchDetails ? (
    <MatchProvider {...match}>
      <MatchDetails {...matchDetails} />
    </MatchProvider>
  ) : undefined;

  return (
    <MatchDetailsContext.Provider
      value={{ MatchDetails: MatchDetailsRendered, populate, reset }}
    >
      {children}
    </MatchDetailsContext.Provider>
  );
});
